import React, { useMemo } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import { SocialAccount } from 'types/influencer';
import { formatNumber } from 'utils/helpers';
import igIcon from 'assets/images/ig.png';
import '../style.scss';

interface SocialAccountFollowTypeProps {
    socialAccount: SocialAccount;
}

interface FollowSplit {
    followers: number;
    nonFollowers: number;
}

interface FollowChart {
    id: string;
    title: string;
    tooltip: string;
    split: FollowSplit;
    total: string;
}

const readFollowSplit = (value: unknown): FollowSplit | null => {
    if (value == null || Array.isArray(value) || typeof value !== 'object') return null;

    const record = value as Record<string, unknown>;
    const followers = Number(record.FOLLOWER);
    const nonFollowers = Number(record.NON_FOLLOWER);
    if (!Number.isFinite(followers) && !Number.isFinite(nonFollowers)) return null;

    return {
        followers: Number.isFinite(followers) ? Math.max(0, followers) : 0,
        nonFollowers: Number.isFinite(nonFollowers) ? Math.max(0, nonFollowers) : 0,
    };
};

const formatTotal = (total: number): string => {
    if (total <= 0) return '0';
    return formatNumber(total);
};

const percentOf = (part: number, total: number): number => {
    if (total <= 0) return 0;
    return Math.round((part / total) * 100);
};

const SocialAccountFollowType: React.FC<SocialAccountFollowTypeProps> = ({ socialAccount }) => {
    const charts = useMemo((): FollowChart[] => {
        const followType = socialAccount.ig?.insights?.follow_type;
        if (!followType || Array.isArray(followType)) return [];

        const accountId = socialAccount.uuid;
        const definitions: Array<Omit<FollowChart, 'split'> & { value: unknown }> = [
            {
                id: `follow-type-views-${accountId}`,
                title: 'Views',
                total: formatNumber(Number(socialAccount.ig?.views_count ?? 0)),
                tooltip: 'Total views in the last 30 days, split by followers and non-followers.',
                value: followType.views,
            },
            {
                id: `follow-type-interactions-${accountId}`,
                title: 'Account interactions',
                tooltip: 'Total account interactions in the last 30 days, split by followers and non-followers.',
                value: followType.account_interactions,
                total: formatNumber(Number(socialAccount.ig?.account_interactions_count ?? 0)),
            },
            {
                id: `follow-type-reach-${accountId}`,
                title: 'Reach',
                tooltip: 'Accounts reached in the last 30 days, split by followers and non-followers.',
                value: followType.reach,
                total: formatNumber(Number(socialAccount.ig?.reach_count ?? 0)),
            },
        ];

        return definitions.flatMap((definition) => {
            const split = readFollowSplit(definition.value);
            if (!split) return [];
            return [{ id: definition.id, title: definition.title, tooltip: definition.tooltip, split, total: definition.total }];
        });
    }, [socialAccount]);

    if (charts.length === 0) return null;

    return (
        <div className="social-account-follow-type">
            {charts.map((chart) => {
                const rows = [
                    { label: 'Non-followers', value: chart.split.nonFollowers },
                    { label: 'Followers', value: chart.split.followers },
                ];

                return (
                    <article className="social-account-follow-type__card" key={chart.id}>
                        <div className="social-account-follow-type__title-row">
                            <img className="social-account-follow-type__logo" src={igIcon} alt="" />
                            <h3 className="social-account-follow-type__title">{chart.title}</h3>
                        </div>
                        <p className="social-account-follow-type__period">Last 30 Days</p>
                        <p className="social-account-follow-type__total">{chart.total}</p>
                        <div className="social-account-follow-type__rows">
                            {rows.map((row) => {
                                const percent = percentOf(row.value, 100);
                                return (
                                    <div className="social-account-insights__bar-group" key={row.label}>
                                        <div className="social-account-insights__bar-header">
                                            <span className="social-account-insights__bar-label">{row.label}</span>
                                            <span className="social-account-insights__bar-value">{percent}%</span>
                                        </div>
                                        <div className="social-account-insights__bar-track">
                                            <div
                                                className="social-account-insights__bar-fill"
                                                style={{ width: `${percent}%` }}
                                                role="progressbar"
                                                aria-valuenow={percent}
                                                aria-valuemin={0}
                                                aria-valuemax={100}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </article>
                );
            })}
        </div>
    );
};

export default SocialAccountFollowType;
